<template>
  <div class="commercialBreakWrapper" :class="{ visible: canShowCommercialBreak }">
    <div class="commercialBreakHeader">
      <div class="timer">Reklama zostanie<br />zamknięta za {{ redirectTime }} sek.</div>
      <button @click.prevent="close">Idź do serwisu</button>
    </div>
    <div class="commercialBreak" v-if="isContentTypeImage">
      <a
        v-if="commercialBreake.targetUrl"
        :href="commercialBreake.targetUrl"
        aria-label="Przejście na stronę reklamy"
        target="_blank"
      >
        <img
          class="commercialBreakeImg"
          :src="commercialBreake.imgSrc"
          :title="commercialBreake.title"
          :alt="commercialBreake.alt"
        />
      </a>
      <div v-if="!commercialBreake.targetUrl">
        <img
          class="commercialBreakeImg"
          :src="commercialBreake.imgSrc"
          :title="commercialBreake.title"
          :alt="commercialBreake.alt"
        />
      </div>
    </div>
    <div class="commercialBreak" v-if="isContentTypeVideo" v-html="commercialBreake.videoContainer"></div>
    <div class="commercialBreak htmlContent" v-if="isContentTypeHtml">
      <iframe
        :src="commercialBreake.htmlUrl"
        :width="commercialBreake.width"
        :height="commercialBreake.height"
        class="ad-banner"
      ></iframe>
    </div>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, onUnmounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { COMMERCIAL_BREAK_REDIRECT_TIME } from '@/base/const/confirmation.const'
import { ToolsClass } from '@/base/class/tools.class'
import { ADS_BANNER_SIZE, ADS_CONTENT_TYPE } from '@/base/const/ads.const'

defineOptions({ name: 'CommercialBreak' })

const store = useStore()
const { proxy } = getCurrentInstance()

let redirectHandler = null
let confirmed = null
const redirectTime = ref(COMMERCIAL_BREAK_REDIRECT_TIME)

if (proxy.$VUE_COMMERCIAL_BREAK_REDIRECT_TIME) {
  redirectTime.value = proxy.$VUE_COMMERCIAL_BREAK_REDIRECT_TIME
}

const transaction = computed(() => store.state.confirmation.data?.transaction)
const showCommercialBreake = computed(() => store.state.ads.showCommercialBreake)
const commercialBreake = computed(() => store.getters.getCommercialBreak)

const canShowCommercialBreak = computed(() => !!commercialBreake.value && showCommercialBreake.value)
const isContentTypeImage = computed(
  () => commercialBreake.value && commercialBreake.value.contentType === ADS_CONTENT_TYPE.image
)
const isContentTypeVideo = computed(
  () => commercialBreake.value && commercialBreake.value.contentType === ADS_CONTENT_TYPE.video
)
const isContentTypeHtml = computed(
  () => commercialBreake.value && commercialBreake.value.contentType === ADS_CONTENT_TYPE.html
)

function sendViewConfirmation() {
  if (!!confirmed && confirmed === commercialBreake.value.creationId) {
    return
  }
  const data = {
    creationId: commercialBreake.value.creationId,
    transRemoteID: transaction.value?.transRemoteID,
  }
  store
    .dispatch('sendAdsBannerViewConfirmation', data)
    .catch(() => {})
    .finally(() => {
      confirmed = commercialBreake.value.creationId
    })
}

function setWindowSize() {
  if (ToolsClass.isMobileScreenSize()) {
    store.commit('SET_ADS_SIZE', ADS_BANNER_SIZE.mobile)
    return
  }
  store.commit('SET_ADS_SIZE', ADS_BANNER_SIZE.desktop)
}

function close() {
  stopRedirectTimeout()
  store.commit('SET_ADS_COMMERCIAL_BREAK_CLOSED', true)
}

function stopRedirectTimeout() {
  if (redirectHandler) {
    clearTimeout(redirectHandler)
  }
}

function startRedirectTimeout() {
  redirectHandler = setTimeout(checkRedirectTimeout, 1000)
}

function checkRedirectTimeout() {
  if (redirectTime.value === 1) {
    close()
    return
  }
  redirectTime.value--
  startRedirectTimeout()
}

function setHeightRecursive(element) {
  // Jeśli nie ma elementu, nic nie rób
  if (!element) return
  // Znajdź div bezpośrednio wewnątrz aktualnego elementu
  const child = element.querySelector(':scope > div, :scope > iframe')
  if (child) {
    // Ustaw block-size: 100% dla znalezionego div
    child.classList.add('fullheight')

    // Rekurencyjnie wykonaj to samo dla tego dziecka
    // Rekurencja tylko jeśli child jest div
    if (child.tagName.toLowerCase() === 'div') {
      setHeightRecursive(child)
    }
  }
}

watch(commercialBreake, (val, oldVal) => {
  if (val && val !== oldVal && isContentTypeVideo.value) {
    ToolsClass.parseAndLoadExternalScriptToHeader(commercialBreake.value.scriptEmbed, () => {
      setTimeout(() => {
        const rootDiv = document.querySelector('div.commercialBreak')
        if (rootDiv) {
          setHeightRecursive(rootDiv)
        }
      }, 1000)
    })
  }
})

watch(canShowCommercialBreak, (val) => {
  if (val && !redirectHandler) {
    if (isContentTypeImage.value || isContentTypeVideo.value || isContentTypeHtml.value) {
      startRedirectTimeout()
      sendViewConfirmation()
    }
  }
})

// nazwane handlery, żeby dało się je zdjąć w onUnmounted (anonimowych nie da się usunąć)
function onWindowResize() {
  setWindowSize()
}

function onWindowKeyup(event) {
  if (event.key === 'Escape') {
    close()
  }
}

onMounted(() => {
  window.addEventListener('resize', onWindowResize)
  window.addEventListener('keyup', onWindowKeyup)
})

onUnmounted(() => {
  window.removeEventListener('resize', onWindowResize)
  window.removeEventListener('keyup', onWindowKeyup)
  stopRedirectTimeout()
})
</script>

<style lang="scss">
@use '../../assets/scss/variables' as *;
@use '../../assets/scss/mixins' as *;

.commercialBreakWrapper {
  position: absolute;
  inline-size: 100%;
  block-size: 100%;
  inset-block-start: 0;
  left: 0;
  z-index: 100;
  background: #000;
  display: none;
  flex-direction: column;
  align-items: center;

  &.visible {
    display: flex;
  }

  .fullheight {
    block-size: 100% !important;
  }

  .commercialBreakHeader {
    background: #fff;
    inline-size: 100%;
    block-size: 70px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px;
    gap: 20px;

    @include sm {
      justify-content: flex-end;
    }

    .timer {
      inline-size: 120px;
      font-size: 11px;
      line-height: 14px;
      color: #000;
    }

    // Przycisk celowo NIE używa klasy `.btn` — jest wersją kompaktową (30 px zamiast 48 px).
    // Kolory bierze z tych samych zmiennych, co reszta przycisków (components/_buttons.scss):
    // wcześniej stało tu `--button-bg-default`, usunięte przy przejściu na design system
    // (PEP-754). Nieistniejąca zmienna daje `background-color: transparent`, więc biały tekst
    // na białym pasku nagłówka robił z przycisku niewidzialny element.
    button {
      background-color: var(--button-static-bg-color);
      block-size: 30px;
      border-radius: var(--button-border-radius);
      border: none;
      padding: 5px 20px;
      font-size: 11px;
      color: var(--button-static-text-color);
      cursor: pointer;

      &:hover {
        background-color: var(--button-hover-bg-color);
        color: var(--button-hover-text-color);
      }
    }
  }

  .commercialBreak {
    inline-size: 100%;
    block-size: calc(100% - 70px);
    padding: 10px;
    display: block;
    text-align: center;

    @include sm {
      inline-size: 80%;
      padding: 40px;
    }

    &.fullscreen {
      a {
        inline-size: 100%;
        block-size: 100%;
      }

      img {
        inline-size: 100%;
        block-size: 100%;
        object-fit: cover;
      }
    }

    &.htmlContent {
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  // Reset design systemu ustawia `img { display: block }`, więc `text-align: center`
  // z `.commercialBreak` na obrazek NIE działa (dotyczy tylko treści inline) i kreacja
  // wisiała przy lewej krawędzi. Wyśrodkowanie musi iść przez marginesy samego obrazka.
  .commercialBreakeImg {
    margin-inline: auto;
  }

  iframe {
    border: 0;
    overflow: hidden;
    margin: 0 auto;
    display: block;
  }
}
</style>
